import { createRoot } from 'react-dom/client';
import { useEffect, useState, useRef } from 'react';
import Swal from '@/react/components/Swal'
import ToasterWrapper from '@/react/components/ToasterWrapper';
const { toast } = ToasterWrapper
import axios from 'axios';

import { RowSelection, Pagination, Table, ColumnEdit } from '@/react/libraries/FkEditTable/';
import useTimeline from '@/react/hooks/useTimeline';
import TableForm from '@/react/components/TableForm';
import Modal from '@/react/libraries/Modal';


// constants
import { COL_TO_PAYLOAD_KEY_MAP, COLUMNS_THAT_FORCE_RELOAD, API_URL, BASE_URL, HEADER_NAMES_CR } from '@/react/pages/qc/base/{utils}/constants';

// functions
import { initStaticPage, initTableHeaders, validateDataOnCellSave, validateAndAddKeysToCellSavePayload, generateDependentDropdown } from '@/react/pages/qc/base/{utils}/functions';
import { LabelButtonCustomizeColumns, LabelButtonRedo, LabelButtonUndo } from '../../../../components/TableActionButtons';
import _ from 'lodash';

const Page = ({ payload }) => {
	// local variables
	const { jurisdiction, keywordColumns, table_url, period_map, visibleColumns, country_id, city_id, specific_retention_values } = payload

	// react hookes, states & refs
	const { addToUndoStack, undo, redo, isUndoStackEmpty, isRedoStackEmpty, clearTimeline } = useTimeline()

	const [headers, setHeaders] = useState([])
	const [isPageEnabled, setIsPageEnabled] = useState(true)
	const [isControlsEnabled, setIsControlsEnabled] = useState(true)
	const [selectedTableRows, setSelectedTableRows] = useState([])
	const [tableUrl, setTableUrl] = useState(`${API_URL}datatable/legal-records-data?page=continuous-review&form_data={}`)
	const [formData, setFormData] = useState({})
	const [temporaryFormData, setTemporaryFormData] = useState({})

	const tableRef = useRef(null)
	const paginationRef = useRef(null)
	const taxonomyDropdownRef = useRef(null)
	const routingNumberInputRef = useRef(null)
	const customizeColumnButtonRef = useRef(null)
	const modalRef = useRef(null)

	const onDropdownOpen = async ({ cell, column, row }) => {
		generateDependentDropdown({ tableRef, cell, column, row })
	}

	const onCellDataSaveHandler = ({
		cell, column, newData: _newData, previousData: _previousData, row, additionalParams = {},
	}) => {
		const { fromTimeline } = additionalParams
		const dataObj = validateDataOnCellSave({
			cell, column, newData: _newData, previousData: _previousData, fromTimeline, tableRef, specific_retention_values, row
		})

		if (!dataObj) return

		const { newData, previousData } = dataObj, cellsToUpdate = {
			routing_number: row.getCell(tableRef.current.columns.findIndex(x => x.heading === 'Routing Number')),
			category_id: row.getCell(tableRef.current.columns.findIndex(x => x.heading === 'Category')),
			subcategory_id: row.getCell(tableRef.current.columns.findIndex(x => x.heading === 'Subcategory')),
			record_type: row.getCell(tableRef.current.columns.findIndex(x => x.heading === 'Record Type')),
		}

		const onSuccess = async () => {
			if (!fromTimeline && previousData) {
				// basically saving manually
				addToUndoStack({ rowIndex: row.dataset.index, columnIndex: column.index, previousData, newData, type: 'normal' }, true)
			}
		}

		let promise;

		if (COLUMNS_THAT_FORCE_RELOAD.includes(column.heading)) {
			const api = `${API_URL}get-interlinked-taxonomy?${COL_TO_PAYLOAD_KEY_MAP[column.heading]}=${newData.value}`
			promise = axios.get(api, {
				headers: window.HEADER_OBJ
			}).then(({ data }) => {
				if (data.status) {
					// update cells
					onSuccess()
					Object.entries(cellsToUpdate).forEach(([key, cell]) => cell.updateData({ value: data.data[key] }, false))
					return 'Taxonomy cells updated successfully'
				} else {
					throw {
						response: {
							data: {
								message: data.message
							}
						}
					}
				}
			}).catch(err => {
				cell.updateData(previousData, false)

				if (err.response) {
					throw err.response.data.message
				} else {
					throw 'Something went wrong'
				}
			})
		}
		else {
			promise = new Promise((resolve, reject) => {
				onSuccess()
				resolve('Cell updated')
			})
		}

		if (additionalParams.noToast) return promise.then(() => { })
		toast.promise(promise, {
			loading: `${fromTimeline ? 'Reverting' : 'Saving'}...`,
			success: x => x,
			error: x => x,
		})
	}

	const applyBulkEdit = (_, undoElement) => {
		const selectedRows = [...selectedTableRows]
		const selectedTaxonomyName = taxonomyDropdownRef.current.getNames();
		const selectedTaxonomyValue = taxonomyDropdownRef.current.getValues();
		const dropdownToColumnNameMap = {
			'category': 'Category',
			'subcategory': 'Subcategory',
			'record_type': 'Record Type',
		}

		if (!undoElement) {
			if (Object.keys(selectedTaxonomyName).length === 0 && !routingNumberInputRef.current.value) return toast.error('Please select a taxonomy/routing number to apply the changes');

			if (selectedRows.length === 0) return toast.error('Please select some rows to apply the changes');

			if (routingNumberInputRef.current.value) {
				if (!window.checkRoutingNumberValidity(routingNumberInputRef.current.value, true))
					return toast.error('Please enter a valid routing number');

				selectedRows.forEach((row) => {
					const cell = row.getCell(tableRef.current.columns.findIndex(x => x.heading === 'Routing Number'))
					cell.updateData({ value: routingNumberInputRef.current.value }, true, { noToast: true })
				})
			} else {
				Object.keys(selectedTaxonomyName).forEach((dropdownKey) => {
					const col = dropdownToColumnNameMap[dropdownKey]
					selectedRows.forEach((row) => {
						const cell = row.getCell(tableRef.current.columns.findIndex(x => x.heading === col))
						cell.updateData({ value: selectedTaxonomyValue[dropdownKey] }, true, { noToast: true })
					})
				})
			}

			toast.success(`Taxonomy updated successfully for ${selectedRows.length} row(s)`)
		}
	}

	const sendToReview = (e) => {
		let payload = {}
		payload['ros'] = selectedTableRows.map((row) => {
			const rowData = HEADER_NAMES_CR.map((header) => {
				const cell = row.getCell(tableRef.current.columns.findIndex(x => x.heading === header));
				const data = cell.getData().value;
				if (header === 'Legal Reference link') {
					// url encode the link
					return [COL_TO_PAYLOAD_KEY_MAP[header], encodeURI(data)];
				}
				return [COL_TO_PAYLOAD_KEY_MAP[header], data];
			});
			const country_id = row.querySelector('[data-country-id]').dataset.countryId
			const city_id = row.querySelector('[data-city-id]').dataset.cityId ?? null

			return Object.fromEntries([
				['fk_ro_id', row.rowId],
				['country_id', country_id],
				['city_id', city_id],
				...rowData
			]);
		})

		payload.title = document.getElementById('issueField').value
		payload.filters = formData

		setIsPageEnabled(false)
		__.loaderButtonInit(e.target)
		const api = `${BASE_URL}admin/cr/reviews`
		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			if (data.status) {
				paginationRef.current.fetchData()
				modalRef.current.closeModal()
				return data.message;
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}
		}).catch(err => {
			if (err.response) {
				throw err.response.data.message
			} else {
				throw "Couldn't send data for review. Please reload your browser"
			}
		}).finally(() => {
			setIsPageEnabled(true)
			e.target.finishLoading()
		})

		toast.promise(promise, {
			loading: 'Sending for review...',
			success: x => x,
			error: x => x,
		})
	}

	const beforeSendForReview = () => {
		const selectedRows = [...selectedTableRows]
		if (selectedRows.length === 0) return toast.error('Please select some rows to send for review')
		modalRef.current.openModal()
	}

	const handleHistoryOperation = (operation, dataKey) => {
		const lastElement = operation();
		if (lastElement?.[dataKey] && lastElement.type === 'normal') {
			const { rowIndex, columnIndex, [dataKey]: data } = lastElement;
			tableRef.current.updateCell(rowIndex, columnIndex, data, true, { fromTimeline: true });
		} else if (lastElement && lastElement.type === 'bulk') {
			toast.error('Bulk edit cannot be undone/redone, your next undo/redo will be a normal cell edit, if any.')
		}
	};

	const handleUndo = () => handleHistoryOperation(undo, 'previousData');
	const handleRedo = () => handleHistoryOperation(redo, 'newData');

	useEffect(() => {
		initStaticPage({
			taxonomyDropdownRef,
			initROCategoryMultiLayer,
		})
		initTableHeaders({
			API_URL,
			setHeaders,
			visibleColumns,
			HEADER_NAMES: HEADER_NAMES_CR,
			period_map,
		})
	}, [])

	return (
		<div className="page-card">
			<h4 className="page-title">
				Continuous Review
			</h4>

			<div disabled={!isPageEnabled || !isControlsEnabled} id='tableForm'>
				<TableForm columnData={keywordColumns} onSubmit={(e, obj) => {
					const form_data = encodeURIComponent(JSON.stringify(obj));
					const url = `${API_URL}datatable/legal-records-data?page=continuous-review&form_data=${form_data}`;
					setFormData(obj)
					setTableUrl(url)
				}} formOnChange={(_, obj)=>{
					setTemporaryFormData(obj)
				}} isSearchDisabled={
					_.isEmpty(temporaryFormData)
				}/>
			</div>
			<div>
				<div className="table-controls" disabled={!isControlsEnabled}>
					<div className="mr-auto flex items-center gap-2">
						<div id="taxonomyDropdown" className="w-full" data-multi-select="false"></div>
						<input className="input-field w-full" placeholder='Routing Number' ref={routingNumberInputRef} />
						<button className="btn btn-wide" onClick={applyBulkEdit}>
							Apply
						</button>
					</div>
					<LabelButtonUndo onClick={handleUndo} disabled={isUndoStackEmpty} />
					<LabelButtonRedo onClick={handleRedo} disabled={isRedoStackEmpty} />
					<LabelButtonCustomizeColumns ref={customizeColumnButtonRef} />

					{/* <a className='label-btn' href={`${BASE_URL}admin/qc/download?country_id=${country_id}&city_id=${city_id}&stage=${stage}`}>
						<span className="fk-icons">download</span>
						Download
					</a> */}
					<button className="btn btn-wide" onClick={beforeSendForReview}>Send for review</button>
				</div>
				<div>
					<Table headers={headers} onCellDataSave={onCellDataSaveHandler}
						//  onCellEdit={onCellEdit}
						onDropdownOpen={onDropdownOpen} ref={tableRef} className={
							`!h-[calc(100dvh-456px)] !min-w-0 !overflow-auto
						[&_[data-header="Select_Row"]]:sticky [&_[data-header="Select_Row"]]:left-0 [&_[data-header="Select_Row"]]:bg-white [&_[data-header="Select_Row"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

						[&_[data-header="Select_All"]]:sticky [&_[data-header="Select_All"]]:left-0 [data-header="Select_All"]]:top-0 [&_[data-header="Select_All"]]:bg-white [&_[data-header="Select_All"]]:z-20 [&_[data-header="Select_All"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

						[&_thead_th[data-header="Action"]]:!text-center
						[&_td[data-header="Legal_Reference_link"]]:break-all [&_td[data-header="What_to_Store"]]:min-w-[250px]`
						} />
					<RowSelection storageObject={setSelectedTableRows} />
					<Pagination className={'pt-6 pr-11'} url={tableUrl} offsetQueryParameter={"start"} lengthQueryParameter={"length"} ref={paginationRef} onPaginate={() => {
						clearTimeline()
					}} willFetch={!_.isEmpty(formData)} />
					<ColumnEdit buttonRef={customizeColumnButtonRef} metaKey="qc_columns" />
				</div>
			</div>
			<Modal ref={modalRef} className={`modal__content-wrapper p-2 `}>
				<div className="modal__content-wrapper-header">
					<h2>Send for review</h2>
				</div>

				<div className="modal__content-wrapper-body">
					<p className='max-w-[600px]'>
                        Please provide a clear title for your work to help reviewers understand its scope..
					</p>

					<div class="text-field">
						<input id="issueField" type="text" name="issue" placeholder="x" />
						<label for="issueField" class="">Name your issue</label>
					</div>
				</div>
				<div className="modal__content-wrapper-footer">
					<button className='btn btn-outline-primary' onClick={modalRef.current?.closeModal}>Cancel</button>
					<button className='btn' onClick={sendToReview}>Send for review</button>
				</div>
			</Modal>
		</div>
	);
}

function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<Page payload={payload} />
	);
}

export default initPage;
